// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * Types for the inventory overview component.
 * This module defines the data structures for displaying inventory status
 * across products and locations in a tabular format.
 */
import { AssetFunctionalStatus } from '@/constants/asset-functional-status';
import { SparseItem, SparseLocation } from '@/sparse/api/types';

/**
 * Asset status counts for a given location/product combination
 */
export const AssetStatusLabels: Record<AssetFunctionalStatus, string> = {
  FUNCTIONAL: 'Okay',
  SCRAPPED: 'Scrapped',
  NEEDS_REVIEW: 'Pending',
  NON_FUNCTIONAL: 'Error',
};

export type AssetStatusCounts = Record<AssetFunctionalStatus, number>;

/**
 * Location data with aggregated asset counts
 */
export interface OverviewLocation {
  /** Location details */
  location: SparseLocation;
  /** Whether this is an inventory or non-inventory location */
  type: 'inventory' | 'non-inventory';
  /** Aggregated asset status counts across all products */
  totalCounts: AssetStatusCounts;
}

/**
 * Product row data with asset counts per location
 */
export interface OverviewProductRow {
  /** Product details */
  product: SparseItem;
  /** Asset counts by location key */
  locationCounts: Record<string, AssetStatusCounts>;
  /** Assets in transit to inventory locations */
  inTransitToInventoryCounts: AssetStatusCounts;
  /** Assets in transit to non-inventory locations */
  inTransitToNonInventoryCounts: AssetStatusCounts;
  /** Total assets in transit (sum of to inventory + to non-inventory) */
  inTransitCounts: AssetStatusCounts;
  /** Total asset counts for this product */
  totalCounts: AssetStatusCounts;
}

/**
 * Aggregated totals for the entire inventory overview
 */
export interface OverviewTotals {
  /** Total counts for inventory locations */
  inventoryTotal: AssetStatusCounts;
  /** Total counts for non-inventory locations */
  nonInventoryTotal: AssetStatusCounts;
  /** Total counts for assets in transit to inventory */
  inTransitToInventoryTotal: AssetStatusCounts;
  /** Total counts for assets in transit to non-inventory */
  inTransitToNonInventoryTotal: AssetStatusCounts;
  /** Total counts for assets in transit (sum of to inventory + to non-inventory) */
  inTransitTotal: AssetStatusCounts;
  /** Grand total across all locations and transit */
  grandTotal: AssetStatusCounts;
}

/**
 * Complete data structure for the inventory overview table
 */
export interface InventoryOverviewData {
  /** Inventory locations (warehouses, company locations, etc.) */
  inventoryLocations: OverviewLocation[];
  /** Non-inventory locations (customer sites, etc.) */
  nonInventoryLocations: OverviewLocation[];
  /** Product rows with asset counts per location */
  productRows: OverviewProductRow[];
  /** Aggregated totals */
  totals: OverviewTotals;
}

/**
 * Props for status indicator component
 */
export interface StatusIndicatorProps {
  /** Status type */
  status: AssetFunctionalStatus;
  /** Count to display */
  count: number;
  /** Click handler for status interaction */
  onClick?: () => void;
  /** Size variant */
  size?: 'small' | 'medium';
  /** Whether to hide the indicator when count is zero */
  hideZero?: boolean;
}

/**
 * Props for asset cell component
 */
export interface AssetCellProps {
  /** Asset status counts to display */
  counts: AssetStatusCounts;
  /** Click handler for status interactions */
  onStatusClick?: (status: AssetFunctionalStatus) => void;
  /** Click handler for total number (no status filter) */
  onTotalClick?: () => void;
  /** Whether this is a totals cell (different styling) */
  isTotal?: boolean;
  /** Whether to show scrapped assets in status indicators */
  showScrappedAssets?: boolean;
  /** Whether to hide zero values instead of graying them out */
  hideZeroes?: boolean;
}
